/* Catalog Showroom engine.
   A dark, tilted 1 m-grid staging floor that presents the catalog's 3D
   pieces three at a time, carousel-style, reworked for a fixed 3-slot stage:
   - pieces are real-world-scale GLBs resting on a shared shadow floor
   - prev/next slides the current trio off along the grid while the next
     trio slides in; GLBs are cached (small LRU); on desktop the neighbour
     page is prefetched so paging feels instant, while touch / low-memory
     devices keep only the visible page resident (mobile Safari kills the
     tab when a page outgrows its memory budget)
   - click selects a piece (ring + camera ease-in + anchored tooltip with
     a product link); drag moves a piece; [ / ] rotates the selection
   - empty-floor drag orbits, wheel / pinch zooms (custom minimal orbit —
     the shared three.js bundle has no OrbitControls)
   - a filter bar (type / brand / in-stock) narrows the piece set
   Requires the shared bundle (catalog-three.min.js) + GLTF loader. */
(function () {
  'use strict';

  var SLIDE = 13; /* minimum off-stage travel for a page change, meters */
  var GRID_EXTENT = 60; /* grid size in meters (1 m divisions) */
  var CLICK_SLOP_PX = 6;
  var MAX_CONCURRENT_LOADS = 4; /* GLB fetches in flight at once */

  /* Critically damped spring step (Unity SmoothDamp) — velocity carries
     between frames so retargeting mid-flight bends the path smoothly. */
  function smoothDampV3(cur, goal, vel, smoothTime, dt) {
    var omega = 2 / smoothTime;
    var x = omega * dt;
    var decay = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
    var ks = ['x', 'y', 'z'];
    for (var i = 0; i < 3; i++) {
      var k = ks[i];
      var change = cur[k] - goal[k];
      var temp = (vel[k] + omega * change) * dt;
      vel[k] = (vel[k] - omega * temp) * decay;
      cur[k] = goal[k] + (change + temp) * decay;
    }
  }
  function smoothDamp1(state, goal, smoothTime, dt) {
    var omega = 2 / smoothTime;
    var x = omega * dt;
    var decay = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
    var change = state.v - goal;
    var temp = (state.vel + omega * change) * dt;
    state.vel = (state.vel - omega * temp) * decay;
    state.v = goal + (change + temp) * decay;
  }

  /* Error boundary: any throw during setup collapses the section to the
     server-rendered fallback list instead of a dead black stage. Runtime
     errors (frame loop, staging) route through the inner fail() so the
     3D scene is also torn down and its memory released. */
  function initShowroom(root) {
    try {
      initShowroomInner(root);
    } catch (err) {
      try {
        console.error('[catalog-showroom] init failed', err);
      } catch (e2) {
        /* console unavailable */
      }
      root.style.minHeight = '';
      root.classList.add('catalog-showroom--no-gl');
    }
  }

  function initShowroomInner(root) {
    if (root.dataset.cshowBound) return;
    root.dataset.cshowBound = 'true';

    var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var stageEl = root.querySelector('[data-cshow-stage]');
    var canvas = root.querySelector('[data-cshow-canvas]');

    var pieces = [];
    try {
      pieces = JSON.parse(root.querySelector('[data-cshow-pieces]').textContent) || [];
    } catch (e) {
      pieces = [];
    }

    function dropStage() {
      root.style.minHeight = ''; /* undo the fitted 100svh inline height */
      root.classList.add('catalog-showroom--no-gl');
    }

    /* Runtime error boundary: tear the 3D stage down (frees GPU memory,
       stops the frame loop) and fall back to the plain product list. */
    function fail(err) {
      try {
        console.error('[catalog-showroom] fatal, dropping to fallback', err);
      } catch (e) {
        /* console unavailable */
      }
      if (root._cshowDestroy) root._cshowDestroy();
      dropStage();
    }

    if (!stageEl || !canvas || !window.THREE || !THREE.GLTFLoader || pieces.length === 0) {
      dropStage();
      return;
    }

    /* Device memory profile. Each GLB decodes to tens of MB of geometry +
       raw texture data, and mobile Safari kills the tab (reload loop, then
       "a problem repeatedly occurred") when the page outgrows its budget.
       Touch devices and low-deviceMemory machines get smaller pages, a
       cache barely larger than one page, no neighbour prefetch, and a
       cheaper renderer. navigator.deviceMemory is undefined on iOS, so
       coarse pointer is the primary signal there. */
    var coarse = window.matchMedia('(pointer: coarse)').matches;
    var deviceMemory = navigator.deviceMemory || 0; /* 0 = unknown */
    var lowPower = coarse || (deviceMemory > 0 && deviceMemory <= 4);

    var renderer;
    try {
      renderer = new THREE.WebGLRenderer({
        canvas: canvas,
        antialias: !lowPower,
        powerPreference: lowPower ? 'low-power' : 'default',
      });
    } catch (e) {
      dropStage();
      return;
    }

    /* ---------- UI refs ---------- */
    var ui = {
      veil: root.querySelector('[data-cshow-veil]'),
      veilLabel: root.querySelector('[data-cshow-veil-label]'),
      tip: root.querySelector('[data-cshow-tip]'),
      tipKicker: root.querySelector('[data-cshow-tip-kicker]'),
      tipTitle: root.querySelector('[data-cshow-tip-title]'),
      tipPrice: root.querySelector('[data-cshow-tip-price]'),
      tipStock: root.querySelector('[data-cshow-tip-stock]'),
      tipLink: root.querySelector('[data-cshow-tip-link]'),
      nudgeButtons: Array.prototype.slice.call(root.querySelectorAll('[data-cshow-nudge]')),
      prev: root.querySelector('[data-cshow-prev]'),
      next: root.querySelector('[data-cshow-next]'),
      chips: root.querySelector('[data-cshow-chips]'),
      counter: root.querySelector('[data-cshow-counter]'),
      count: root.querySelector('[data-cshow-count]'),
      clearBtn: root.querySelector('[data-cshow-clear]'),
      empty: root.querySelector('[data-cshow-empty]'),
      dds: Array.prototype.slice.call(root.querySelectorAll('[data-cshow-dd]')),
    };

    var documentTokens = window.getComputedStyle(document.documentElement);
    var accent = root.dataset.accent || documentTokens.getPropertyValue('--catalog-camel').trim();
    var bg = root.dataset.bg || documentTokens.getPropertyValue('--catalog-showroom-background').trim();
    var white = new THREE.Color(documentTokens.getPropertyValue('--catalog-white').trim());
    var pageSize = Math.max(1, parseInt(root.dataset.pageSize, 10) || 3);
    if (lowPower) pageSize = Math.min(pageSize, 3);
    var cacheMax = lowPower ? pageSize + 2 : pageSize * 2 + 3;
    var maxLoads = lowPower ? 2 : MAX_CONCURRENT_LOADS;
    var prefetchNeighbours = !lowPower;

    /* ---------- scene ---------- */
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, lowPower ? 1.5 : 2));
    renderer.outputEncoding = THREE.sRGBEncoding;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    var scene = new THREE.Scene();
    var bgColor = new THREE.Color(bg);
    scene.background = bgColor.clone();
    scene.fog = new THREE.Fog(bgColor.clone(), 16, 38);

    var camera = new THREE.PerspectiveCamera(lowPower ? 42 : 38, 1, 0.05, 140);

    /* Soft studio environment for PBR reflections: a dark box with bright
       overhead / side panels run through PMREM (the bundle has no
       RoomEnvironment, so this is a minimal stand-in).
       AUDIT-IGNORE: non-token WebGL literals below are calibrated light,
       environment, and grid colors rather than interface palette values.
       The exact brand-white light source is read from generated CSS tokens. */
    var envTexture = null;
    (function buildEnv() {
      try {
        var envScene = new THREE.Scene();
        var roomMat = new THREE.MeshBasicMaterial({ color: 0x1c1c20, side: THREE.BackSide });
        var room = new THREE.Mesh(new THREE.BoxGeometry(24, 14, 24), roomMat);
        room.position.y = 6;
        envScene.add(room);
        var panel = function (w, h, x, y, z, rx, ry, level) {
          var m = new THREE.Mesh(
            new THREE.PlaneGeometry(w, h),
            new THREE.MeshBasicMaterial({ color: new THREE.Color(level, level, level) }),
          );
          m.position.set(x, y, z);
          m.rotation.set(rx, ry, 0);
          envScene.add(m);
        };
        panel(6, 6, 0, 12.9, 0, Math.PI / 2, 0, 5); /* ceiling key */
        panel(4, 3, -10, 6, 0, 0, Math.PI / 2, 2.2); /* left fill */
        panel(4, 3, 10, 5, 2, 0, -Math.PI / 2, 1.4); /* right rim */
        var pmrem = new THREE.PMREMGenerator(renderer);
        envTexture = pmrem.fromScene(envScene, 0.04).texture;
        pmrem.dispose();
        scene.environment = envTexture;
      } catch (e) {
        /* env is a nicety — lights below carry the scene without it */
      }
    })();

    scene.add(new THREE.HemisphereLight(white, 0x35322e, 0.5));
    var sun = new THREE.DirectionalLight(white, 1.15);
    sun.position.set(6, 10, 4);
    sun.castShadow = true;
    sun.shadow.mapSize.set(lowPower ? 1024 : 2048, lowPower ? 1024 : 2048);
    /* Wide enough that trios sliding off-stage (±SLIDE) keep shadows. */
    sun.shadow.camera.left = -16;
    sun.shadow.camera.right = 16;
    sun.shadow.camera.top = 16;
    sun.shadow.camera.bottom = -16;
    sun.shadow.camera.updateProjectionMatrix();
    scene.add(sun);
    var rim = new THREE.DirectionalLight(0xf3e2cd, 0.3);
    rim.position.set(-5, 3, -4);
    scene.add(rim);

    var floorMesh = new THREE.Mesh(
      new THREE.PlaneGeometry(GRID_EXTENT * 2, GRID_EXTENT * 2),
      new THREE.ShadowMaterial({ opacity: 0.5 }),
    );
    floorMesh.rotation.x = -Math.PI / 2;
    floorMesh.receiveShadow = true;
    scene.add(floorMesh);

    var grid = new THREE.GridHelper(
      GRID_EXTENT,
      GRID_EXTENT,
      new THREE.Color(accent).multiplyScalar(0.55),
      new THREE.Color(0x3a393f),
    );
    grid.material.transparent = true;
    grid.material.opacity = 0.42;
    scene.add(grid);

    var ring = new THREE.Mesh(
      new THREE.RingGeometry(1, 1.06, 48),
      new THREE.MeshBasicMaterial({
        color: new THREE.Color(accent),
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide,
      }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.01;
    ring.visible = false;
    scene.add(ring);
    var ringHandle = new THREE.Mesh(
      new THREE.CircleGeometry(0.075, 16),
      new THREE.MeshBasicMaterial({ color: new THREE.Color(accent), side: THREE.DoubleSide }),
    );
    ringHandle.rotation.x = -Math.PI / 2;
    ringHandle.position.y = 0.02;
    ringHandle.visible = false;
    scene.add(ringHandle);

    /* ---------- custom orbit ----------
       Spherical coordinates around a floor target; drag steers the goal
       angles, wheel/pinch the goal distance, and everything is damped in
       the frame loop. Focus/home moves retarget the same goals. */
    /* Intro pose: higher and further out than home, so the first staging
       reads as a descent onto the floor. */
    var INITIAL_THETA = 0.5;
    var PRESENTATION_BACK_OFFSET = 0.7;
    var orbit = {
      theta: 0.95,
      phi: 0.92,
      target: new THREE.Vector3(0, 0.55, 0),
      goalTheta: INITIAL_THETA,
      goalPhi: 1.2,
      dist: { v: 26, vel: 0 },
      goalDist: 10,
      velTarget: new THREE.Vector3(),
      targetSmooth: 0.4,
    };
    var MIN_PHI = 0.35;
    var MAX_PHI = 1.42;
    var MIN_DIST = 3;
    var MAX_DIST = 30;
    var goalTargetV = new THREE.Vector3(0, 0.55, 0);

    function applyCamera() {
      var sp = Math.sin(orbit.phi);
      camera.position.set(
        orbit.target.x + orbit.dist.v * sp * Math.sin(orbit.theta),
        orbit.target.y + orbit.dist.v * Math.cos(orbit.phi),
        orbit.target.z + orbit.dist.v * sp * Math.cos(orbit.theta),
      );
      camera.lookAt(orbit.target);
    }

    function frameRow(dims, ms) {
      var vFov = (camera.fov * Math.PI) / 180;
      var hFov = 2 * Math.atan(Math.tan(vFov / 2) * Math.max(0.6, camera.aspect));
      /* Bounding-circle fit: multi-row layouts have depth, so frame the
         floor footprint's radius, not just the row width. */
      var radius = Math.hypot(dims.width / 2, dims.depth / 2);
      var distW = (radius + 1) / Math.tan(hFov / 2);
      var distH = (dims.height + 0.8) / Math.tan(vFov / 2) + dims.depth / 2;
      var dist = Math.min(MAX_DIST, Math.max(5, Math.max(distW, distH)));
      /* Push the fog back with the framing so back rows on a large floor
         stay crisp — it only ever recedes, so orbiting stays consistent. */
      var fogNear = Math.max(16, dist + radius + 2);
      if (fogNear > scene.fog.near) {
        scene.fog.near = fogNear;
        scene.fog.far = fogNear + 22;
      }
      goalTargetV.set(0, Math.min(1, 0.3 + dims.height * 0.28), 0);
      orbit.goalDist = dist;
      /* Only re-aim the angles until the visitor grabs the orbit — after
         that, paging keeps their viewpoint and just re-frames distance. */
      if (!userDrove) {
        orbit.goalTheta = INITIAL_THETA;
        orbit.goalPhi = 1.2;
      }
      orbit.targetSmooth = (ms || 1100) / 3000;
      if (reduced) {
        orbit.theta = orbit.goalTheta;
        orbit.phi = orbit.goalPhi;
        orbit.dist.v = orbit.goalDist;
        orbit.target.copy(goalTargetV);
      }
    }

    function focusRoot(r) {
      var pos = r.group.position;
      goalTargetV.set(pos.x, Math.min(0.9, 0.25 + r.sizeY * 0.3), pos.z);
      orbit.goalDist = Math.max(3.4, r.radius * 4.4);
      orbit.targetSmooth = 0.32;
      if (reduced) {
        orbit.dist.v = orbit.goalDist;
        orbit.target.copy(goalTargetV);
      }
    }

    /* ---------- GLB cache (LRU) ---------- */
    var loadCache = new Map(); /* piece.id -> { promise, scene } */

    function disposeObject(o) {
      o.traverse(function (node) {
        if (node.geometry) node.geometry.dispose();
        var mats = Array.isArray(node.material) ? node.material : node.material ? [node.material] : [];
        mats.forEach(function (m) {
          for (var k in m) {
            if (m[k] && m[k].isTexture) m[k].dispose();
          }
          m.dispose();
        });
      });
    }

    /* GLB downloads run through a small concurrency-limited queue: a large
       page firing every request at once would saturate the connection pool
       and delay the first pieces from reaching the floor. */
    var loadsInFlight = 0;
    var loadQueue = [];
    function pumpLoads() {
      while (loadsInFlight < maxLoads && loadQueue.length) {
        var task = loadQueue.shift();
        loadsInFlight++;
        task(function () {
          loadsInFlight--;
          pumpLoads();
        });
      }
    }

    function loadPiece(piece) {
      var hit = loadCache.get(piece.id);
      if (hit) {
        /* refresh LRU position */
        loadCache.delete(piece.id);
        loadCache.set(piece.id, hit);
        return hit.promise;
      }
      var entry = {};
      entry.promise = new Promise(function (resolve) {
        loadQueue.push(function (done) {
          if (!piece.model) {
            resolve(null);
            done();
            return;
          }
          new THREE.GLTFLoader().load(
            piece.model,
            function (gltf) {
              entry.scene = gltf.scene;
              resolve(gltf.scene);
              done();
            },
            undefined,
            function () {
              resolve(null);
              done();
            },
          );
        });
        pumpLoads();
      });
      loadCache.set(piece.id, entry);
      return entry.promise;
    }

    function evictCache(keepIds) {
      var keys = Array.from(loadCache.keys());
      for (var i = 0; i < keys.length && loadCache.size > cacheMax; i++) {
        var id = keys[i];
        if (keepIds.indexOf(id) !== -1) continue;
        var entry = loadCache.get(id);
        loadCache.delete(id);
        if (entry && entry.scene) disposeObject(entry.scene);
      }
    }

    /* ---------- staging ---------- */
    var staged = []; /* current trio roots */
    var ghosts = []; /* roots sliding off-stage */
    var selectedRoot = null;
    var stageToken = 0;
    var userDrove = false;
    var stageReady = false;

    function makeRoot(piece, source) {
      var clone = source.clone(true);
      clone.traverse(function (o) {
        if (o.isMesh) {
          o.castShadow = true;
          o.receiveShadow = true;
        }
      });
      var group = new THREE.Group();
      group.add(clone);
      var box = new THREE.Box3().setFromObject(clone);
      var size = box.getSize(new THREE.Vector3());
      var maxDim = Math.max(size.x, size.y, size.z) || 1;
      /* Catalog GLBs are authored in meters; guard against outliers so one
         bad export can't dwarf (or vanish beside) its neighbours. */
      if (maxDim > 8 || maxDim < 0.4) {
        clone.scale.multiplyScalar(2.4 / maxDim);
        box.setFromObject(clone);
        box.getSize(size);
      }
      var center = box.getCenter(new THREE.Vector3());
      clone.position.x -= center.x;
      clone.position.z -= center.z;
      clone.position.y -= box.min.y;
      return {
        piece: piece,
        group: group,
        sizeX: size.x,
        sizeY: size.y,
        sizeZ: size.z,
        radius: Math.max(size.x, size.z) / 2 + 0.15,
        baseGoal: new THREE.Vector3(),
        offset: new THREE.Vector3(),
        goal: new THREE.Vector3(),
        vel: new THREE.Vector3(),
        autoRotationY: 0,
        manualRotation: false,
        settled: false,
      };
    }

    /* ---------- filters & paging ---------- */
    var restoredState = readShowroomState();
    var filters = {
      type: restoredState.type,
      vendor: restoredState.vendor,
      stock: restoredState.stock,
    };
    var filtered = pieces.slice();
    var page = restoredState.page;

    function applyFilters() {
      filtered = pieces.filter(function (p) {
        if (filters.type && p.type !== filters.type) return false;
        if (filters.vendor && p.vendor !== filters.vendor) return false;
        if (filters.stock === 'in_stock' && !p.available) return false;
        if (filters.stock === 'sold_out' && p.available) return false;
        return true;
      });
    }

    function pageCount() {
      return Math.max(1, Math.ceil(filtered.length / pageSize));
    }
    function pagePieces(n) {
      return filtered.slice(n * pageSize, n * pageSize + pageSize);
    }

    function setVeil(on, label) {
      if (!ui.veil) return;
      if (label && ui.veilLabel) ui.veilLabel.textContent = label;
      ui.veil.classList.toggle('is-visible', Boolean(on));
    }

    function syncNav() {
      var pages = pageCount();
      if (ui.prev) ui.prev.disabled = page <= 0 || filtered.length === 0;
      if (ui.next) ui.next.disabled = page >= pages - 1 || filtered.length === 0;
      if (ui.counter) {
        if (filtered.length === 0) {
          ui.counter.textContent = '';
        } else {
          var a = page * pageSize + 1;
          var b = Math.min(filtered.length, a + pageSize - 1);
          ui.counter.textContent = a + '\u2013' + b + ' ' + root.dataset.labelRangeOf + ' ' + filtered.length;
        }
      }
      if (ui.count) {
        ui.count.textContent =
          filtered.length +
          ' ' +
          (filtered.length === 1 ? root.dataset.labelPieceSingular : root.dataset.labelPiecePlural);
      }
      if (ui.empty) ui.empty.hidden = filtered.length > 0;
    }

    function syncChips() {
      if (!ui.chips) return;
      ui.chips.innerHTML = '';
      staged.forEach(function (r) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'cshow__chip' + (selectedRoot === r ? ' is-active' : '');
        btn.setAttribute('aria-label', root.dataset.labelFocus + ' ' + r.piece.title);
        btn.setAttribute('aria-pressed', String(selectedRoot === r));
        if (r.piece.thumb) {
          var img = document.createElement('img');
          img.src = r.piece.thumb;
          img.alt = r.piece.title;
          img.loading = 'lazy';
          btn.appendChild(img);
        }
        btn.addEventListener('click', function () {
          select(r, true);
        });
        ui.chips.appendChild(btn);
      });
    }

    function readShowroomState() {
      var state = { type: '', vendor: '', stock: '', page: 0, piece: '' };
      try {
        var hash = window.location.hash;
        var params;
        if (hash.indexOf('#showroom?') === 0) {
          params = new URLSearchParams(hash.slice('#showroom?'.length));
        } else {
          /* Preserve existing ?piece= links while moving new state to the hash. */
          params = new URL(window.location.href).searchParams;
        }
        state.type = params.get('type') || '';
        state.vendor = params.get('vendor') || '';
        state.stock =
          params.get('stock') === 'in_stock' || params.get('stock') === 'sold_out' ? params.get('stock') : '';
        state.page = Math.max(0, parseInt(params.get('page') || '0', 10) || 0);
        state.piece = params.get('piece') || '';
      } catch (e) {
        /* URL state is an enhancement; defaults still produce a working showroom. */
      }
      return state;
    }

    function syncShowroomState() {
      try {
        var url = new URL(window.location.href);
        var params = new URLSearchParams();
        if (filters.type) params.set('type', filters.type);
        if (filters.vendor) params.set('vendor', filters.vendor);
        if (filters.stock) params.set('stock', filters.stock);
        if (page) params.set('page', String(page));
        if (selectedRoot) params.set('piece', selectedRoot.piece.id);
        url.searchParams.delete('piece');
        url.hash = 'showroom?' + params.toString();
        window.history.replaceState(window.history.state, '', url);
      } catch (e) {
        /* URL API unavailable — state remains local to this page view. */
      }
    }

    /* ---------- selection + tooltip ---------- */
    function select(r, focus) {
      selectedRoot = r || null;
      if (r) {
        ring.visible = true;
        ring.scale.setScalar(r.radius);
        if (ui.tipKicker) ui.tipKicker.textContent = [r.piece.vendor, r.piece.type].filter(Boolean).join(' \u00b7 ');
        if (ui.tipTitle) ui.tipTitle.textContent = r.piece.title;
        if (ui.tipPrice) ui.tipPrice.textContent = r.piece.price;
        if (ui.tipStock) {
          ui.tipStock.textContent = r.piece.available ? root.dataset.labelInStock : root.dataset.labelSoldOut;
          ui.tipStock.className = 'cshow__tip-stock ' + (r.piece.available ? 'is-in' : 'is-out');
        }
        if (ui.tipLink) ui.tipLink.href = r.piece.url;
        if (focus) focusRoot(r);
        syncShowroomState();
      } else {
        ring.visible = false;
        syncShowroomState();
      }
      syncChips();
    }

    var tipVec = new THREE.Vector3();
    function updateTip() {
      if (!ui.tip) return;
      var visible = false;
      if (selectedRoot && ring.visible && !draggingPiece) {
        tipVec.set(selectedRoot.group.position.x, selectedRoot.sizeY + 0.1, selectedRoot.group.position.z);
        tipVec.project(camera);
        if (tipVec.z < 1) {
          var x = (tipVec.x * 0.5 + 0.5) * stageEl.clientWidth;
          var y = (-tipVec.y * 0.5 + 0.5) * stageEl.clientHeight;
          ui.tip.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0)';
          visible = true;
        }
      }
      if (ui.tip.dataset.visible !== String(visible)) {
        ui.tip.dataset.visible = String(visible);
      }
    }

    /* ---------- page transitions ---------- */

    /* Initial staging is a compact front-facing gallery. The selection ring
       remains available for hands-on rotation without forcing every sofa
       into an artificial circular pose before the shopper interacts. */
    function layoutFloor(roots) {
      var count = roots.length;
      if (!count) return { width: 0, depth: 0, height: 0 };

      var largestFootprint = 0;
      var maxY = 0;
      roots.forEach(function (r) {
        largestFootprint = Math.max(largestFootprint, r.sizeX, r.sizeZ);
        maxY = Math.max(maxY, r.sizeY);
      });

      var mobileArc = lowPower && count <= 3;
      var mobileSpacing = Math.max(1.35, largestFootprint * 0.88);
      var galleryColumns = Math.min(3, count);
      var galleryRows = Math.ceil(count / galleryColumns);
      var gallerySpacingX = Math.max(1.65, largestFootprint * 1.12);
      var gallerySpacingZ = Math.max(1.55, largestFootprint * 0.9);
      var extent = 0;
      roots.forEach(function (r, index) {
        var row = Math.floor(index / galleryColumns);
        var rowStart = row * galleryColumns;
        var rowCount = Math.min(galleryColumns, count - rowStart);
        var column = index - rowStart;
        var x = mobileArc ? (index - (count - 1) / 2) * mobileSpacing : (column - (rowCount - 1) / 2) * gallerySpacingX;
        var z = mobileArc ? Math.abs(x) * 0.16 : (row - (galleryRows - 1) / 2) * gallerySpacingZ;
        var fanX = x;
        x -= Math.sin(INITIAL_THETA) * PRESENTATION_BACK_OFFSET;
        z -= Math.cos(INITIAL_THETA) * PRESENTATION_BACK_OFFSET;
        var heading = orbit.goalTheta + (mobileArc ? (fanX / mobileSpacing) * 0.14 : (fanX / gallerySpacingX) * 0.08);
        r.baseGoal.set(x, 0, z);
        r.goal.copy(r.baseGoal).add(r.offset);
        if (!r.manualRotation) {
          r.autoRotationY = heading;
          r.group.rotation.y = heading;
        }
        extent = Math.max(extent, Math.abs(r.goal.x) + r.sizeX / 2, Math.abs(r.goal.z) + r.sizeZ / 2);
      });
      return { width: extent * 2, depth: extent * 2, height: maxY };
    }

    var lastLayoutWidth = 0;
    var dragLim = 8;

    function setManualPosition(r, x, z) {
      var lim = dragLim;
      x = Math.max(-lim, Math.min(lim, x));
      z = Math.max(-lim, Math.min(lim, z));
      r.group.position.set(x, 0, z);
      r.offset.copy(r.group.position).sub(r.baseGoal);
      r.goal.copy(r.group.position);
    }

    function nudgeSelected(x, z) {
      if (!selectedRoot) return;
      setManualPosition(selectedRoot, selectedRoot.group.position.x + x, selectedRoot.group.position.z + z);
    }

    function goToPage(n, dir) {
      var pages = pageCount();
      n = Math.max(0, Math.min(pages - 1, n));
      page = n;
      var token = ++stageToken;
      select(null, false);
      syncNav();

      /* slide the current pieces off-stage, clear of the incoming layout */
      var slideOut = Math.max(SLIDE, lastLayoutWidth + 10);
      staged.forEach(function (r) {
        r.goal.x -= dir * slideOut;
        ghosts.push(r);
      });
      staged = [];
      syncChips();

      var items = pagePieces(n);
      if (items.length === 0) {
        setVeil(false);
        return;
      }

      /* Pieces stream onto the floor as each GLB arrives (the layout
         re-flows around them), so a large page starts filling immediately
         instead of waiting for every download. */
      setVeil(true, root.dataset.labelStaging);
      var remaining = items.length;
      items.forEach(function (p, order) {
        loadPiece(p).then(function (src) {
          try {
            stagePiece(src);
          } catch (err) {
            /* a bad GLB (or an OOM throw while cloning it) must not
               strand the veil or become an unhandled rejection */
            fail(err);
          }
        });

        function stagePiece(src) {
          if (token !== stageToken) return;
          remaining--;
          if (src) {
            var r = makeRoot(p, src);
            r.order = order;
            staged.push(r);
            staged.sort(function (a, b) {
              return a.order - b.order;
            });
            var dims = layoutFloor(staged);
            lastLayoutWidth = dims.width;
            dragLim = Math.max(8, dims.width / 2 + 4);
            r.group.position.copy(r.goal);
            if (!reduced && dir !== 0) {
              r.group.position.x += dir * Math.max(SLIDE, dims.width + 10);
            }
            scene.add(r.group);
            if (!stageReady) {
              stageReady = true;
              stageEl.classList.add('is-ready');
            }
            setVeil(false);
            frameRow(dims, dir === 0 ? 1500 : 1100);
            syncChips();
          }
          if (remaining === 0) {
            setVeil(false);
            var keep = items.map(function (it) {
              return it.id;
            });
            if (!prefetchNeighbours) {
              /* Low-power devices hold only the visible page (plus slack in
                 cacheMax); anything else is dropped right away so memory
                 stays flat no matter how many pages get visited. */
              evictCache(keep);
              return;
            }
            /* Desktop: prefetch the next page one GLB at a time (queued
               behind any interactive loads), then trim the cache. */
            if (n > 0) {
              pagePieces(n - 1).forEach(function (it) {
                keep.push(it.id); /* likely still cached — keep, don't load */
              });
            }
            var chain = Promise.resolve();
            if (n + 1 < pageCount()) {
              pagePieces(n + 1).forEach(function (it) {
                keep.push(it.id);
                chain = chain.then(function () {
                  return token === stageToken ? loadPiece(it) : null;
                });
              });
            }
            chain.then(function () {
              evictCache(keep);
            });
          }
        }
      });
    }

    /* ---------- filter UI ---------- */
    function facetCounts(key) {
      if (key === 'stock') {
        var stockCounts = { in_stock: 0, sold_out: 0 };
        pieces.forEach(function (p) {
          if (filters.type && p.type !== filters.type) return;
          if (filters.vendor && p.vendor !== filters.vendor) return;
          stockCounts[p.available ? 'in_stock' : 'sold_out']++;
        });
        return stockCounts;
      }

      var counts = {};
      pieces.forEach(function (p) {
        if (filters.stock === 'in_stock' && !p.available) return;
        if (filters.stock === 'sold_out' && p.available) return;
        var other = key === 'type' ? 'vendor' : 'type';
        if (filters[other] && p[other] !== filters[other]) return;
        var v = p[key];
        if (!v) return;
        counts[v] = (counts[v] || 0) + 1;
      });
      return counts;
    }

    function buildMenus() {
      ui.dds.forEach(function (dd) {
        var key = dd.dataset.cshowDd;
        var menu = dd.querySelector('[data-cshow-dd-menu]');
        var label = dd.querySelector('[data-cshow-dd-label]');
        if (!menu) return;
        var counts = facetCounts(key);
        var isStock = key === 'stock';
        var values = isStock
          ? ['in_stock', 'sold_out']
          : Object.keys(counts).sort(function (a, b) {
              return counts[b] - counts[a] || a.localeCompare(b);
            });
        menu.innerHTML = '';
        var allLabel = isStock
          ? root.dataset.labelAllStock
          : key === 'type'
            ? root.dataset.labelAllTypes
            : root.dataset.labelAllBrands;
        var displayValue = function (value) {
          if (value === 'in_stock') return root.dataset.labelInStock;
          if (value === 'sold_out') return root.dataset.labelSoldOut;
          return value;
        };
        var addItem = function (value, text, count) {
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'cshow__dd-item' + ((filters[key] || '') === value ? ' is-active' : '');
          var name = document.createElement('span');
          name.textContent = text;
          btn.appendChild(name);
          if (count != null) {
            var n = document.createElement('span');
            n.className = 'cshow__dd-n';
            n.textContent = count;
            btn.appendChild(n);
          }
          btn.addEventListener('click', function () {
            filters[key] = value;
            dd.removeAttribute('open');
            onFiltersChanged();
          });
          menu.appendChild(btn);
        };
        addItem('', allLabel, null);
        values.forEach(function (v) {
          addItem(v, displayValue(v), counts[v]);
        });
        if (label) label.textContent = filters[key] ? displayValue(filters[key]) : allLabel;
        dd.classList.toggle('is-active', Boolean(filters[key]));
      });
      if (ui.clearBtn) {
        ui.clearBtn.hidden = !(filters.type || filters.vendor || filters.stock);
      }
    }

    function onFiltersChanged() {
      applyFilters();
      buildMenus();
      goToPage(0, 1);
    }

    if (ui.clearBtn) {
      ui.clearBtn.addEventListener('click', function () {
        filters.type = '';
        filters.vendor = '';
        filters.stock = '';
        onFiltersChanged();
      });
    }
    /* one dropdown open at a time; click-away closes */
    ui.dds.forEach(function (dd) {
      dd.addEventListener('toggle', function () {
        if (!dd.open) return;
        ui.dds.forEach(function (other) {
          if (other !== dd) other.removeAttribute('open');
        });
      });
    });
    function onDocumentClick(e) {
      ui.dds.forEach(function (dd) {
        if (dd.open && !dd.contains(e.target)) dd.removeAttribute('open');
      });
    }
    document.addEventListener('click', onDocumentClick);

    if (ui.prev) {
      ui.prev.addEventListener('click', function () {
        goToPage(page - 1, -1);
      });
    }
    if (ui.next) {
      ui.next.addEventListener('click', function () {
        goToPage(page + 1, 1);
      });
    }
    ui.nudgeButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        nudgeSelected((parseFloat(button.dataset.x) || 0) * 0.2, (parseFloat(button.dataset.z) || 0) * 0.2);
      });
    });

    /* ---------- pointer interaction ---------- */
    var raycaster = new THREE.Raycaster();
    var pointerNdc = new THREE.Vector2();
    var floorPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
    var hitV = new THREE.Vector3();
    var grabOffset = new THREE.Vector3();
    var press = null; /* { id, x, y, root, theta, phi } */
    var draggingPiece = false;
    var rotatingPiece = false;
    var panningFloor = false;
    var orbiting = false;
    var pinch = null; /* { d, dist } */
    var activePointers = new Map();

    function setPointer(e) {
      var rect = canvas.getBoundingClientRect();
      pointerNdc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointerNdc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(pointerNdc, camera);
    }

    function pickRoot(forgiving) {
      var groups = staged.map(function (r) {
        return r.group;
      });
      var hits = raycaster.intersectObjects(groups, true);
      if (hits.length) {
        var obj = hits[0].object;
        while (obj && groups.indexOf(obj) === -1) obj = obj.parent;
        for (var i = 0; i < staged.length; i++) {
          if (staged[i].group === obj) return staged[i];
        }
      }
      if (!forgiving) return null;
      if (!raycaster.ray.intersectPlane(floorPlane, hitV)) return null;
      var best = null;
      var bestDist = Infinity;
      staged.forEach(function (r) {
        var d = Math.hypot(r.group.position.x - hitV.x, r.group.position.z - hitV.z);
        if (d < r.radius + 0.25 && d < bestDist) {
          best = r;
          bestDist = d;
        }
      });
      return best;
    }

    function pickRotationRing() {
      if (!selectedRoot || !ring.visible) return null;
      if (!raycaster.ray.intersectPlane(floorPlane, hitV)) return null;
      var dx = hitV.x - selectedRoot.group.position.x;
      var dz = hitV.z - selectedRoot.group.position.z;
      var distance = Math.hypot(dx, dz);
      var radius = selectedRoot.radius;
      if (distance < radius * 0.72 || distance > radius * 1.4) return null;
      return Math.atan2(dx, dz);
    }

    function onPointerDown(e) {
      if (e.button !== undefined && e.button !== 0 && e.button !== 2) return;
      activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (activePointers.size === 2) {
        /* second finger — pinch zoom, cancel any click/drag in progress */
        var pts = Array.from(activePointers.values());
        pinch = { d: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y), dist: orbit.goalDist };
        press = null;
        draggingPiece = false;
        rotatingPiece = false;
        panningFloor = false;
        orbiting = false;
        return;
      }
      setPointer(e);
      if (e.button === 2) {
        raycaster.ray.intersectPlane(floorPlane, hitV);
        press = {
          id: e.pointerId,
          x: e.clientX,
          y: e.clientY,
          mode: 'pan',
          floorStart: hitV.clone(),
          targetStart: goalTargetV.clone(),
        };
        canvas.setPointerCapture && canvas.setPointerCapture(e.pointerId);
        canvas.classList.add('is-grabbing');
        return;
      }
      var ringAngle = pickRotationRing();
      var r = ringAngle == null ? pickRoot(e.pointerType === 'touch') : selectedRoot;
      press = {
        id: e.pointerId,
        x: e.clientX,
        y: e.clientY,
        root: r,
        mode: ringAngle == null ? (r ? 'drag' : 'orbit') : 'rotate',
        startAngle: ringAngle,
        startRotation: r ? r.group.rotation.y : 0,
        theta: orbit.goalTheta,
        phi: orbit.goalPhi,
      };
      if (r && press.mode === 'drag') {
        raycaster.ray.intersectPlane(floorPlane, hitV);
        grabOffset.copy(r.group.position).sub(hitV);
      }
      canvas.setPointerCapture && canvas.setPointerCapture(e.pointerId);
      canvas.classList.add('is-grabbing');
    }

    function onPointerMove(e) {
      var tracked = activePointers.get(e.pointerId);
      if (tracked) {
        tracked.x = e.clientX;
        tracked.y = e.clientY;
      }
      if (pinch && activePointers.size === 2) {
        var pts = Array.from(activePointers.values());
        var d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        if (d > 0) {
          orbit.goalDist = Math.max(MIN_DIST, Math.min(MAX_DIST, (pinch.dist * pinch.d) / d));
          userDrove = true;
        }
        return;
      }
      if (!press || e.pointerId !== press.id) return;
      var dx = e.clientX - press.x;
      var dy = e.clientY - press.y;
      var moved = Math.hypot(dx, dy) > CLICK_SLOP_PX;

      if (press.mode === 'pan') {
        if (!moved && !panningFloor) return;
        panningFloor = true;
        userDrove = true;
        setPointer(e);
        if (raycaster.ray.intersectPlane(floorPlane, hitV)) {
          goalTargetV.copy(press.targetStart).sub(hitV.sub(press.floorStart));
        }
        return;
      }

      if (press.mode === 'rotate') {
        if (!moved && !rotatingPiece) return;
        if (!rotatingPiece) {
          rotatingPiece = true;
          select(press.root, false);
        }
        setPointer(e);
        if (raycaster.ray.intersectPlane(floorPlane, hitV)) {
          var ringAngle = Math.atan2(hitV.x - press.root.group.position.x, hitV.z - press.root.group.position.z);
          press.root.group.rotation.y = press.startRotation + ringAngle - press.startAngle;
          press.root.manualRotation = true;
        }
        return;
      }

      if (press.root) {
        if (!moved && !draggingPiece) return;
        if (!draggingPiece) {
          draggingPiece = true;
          select(press.root, false); /* ring tracks the piece; camera stays */
        }
        setPointer(e);
        if (raycaster.ray.intersectPlane(floorPlane, hitV)) {
          setManualPosition(press.root, hitV.x + grabOffset.x, hitV.z + grabOffset.z);
        }
        return;
      }

      if (!moved && !orbiting) return;
      orbiting = true;
      userDrove = true;
      orbit.goalTheta = press.theta - dx * 0.0055;
      orbit.goalPhi = Math.max(MIN_PHI, Math.min(MAX_PHI, press.phi - dy * 0.004));
    }

    function endPointer(e, cancelled) {
      activePointers.delete(e.pointerId);
      if (activePointers.size < 2) pinch = null;
      if (!press || e.pointerId !== press.id) {
        if (activePointers.size === 0) canvas.classList.remove('is-grabbing');
        return;
      }
      var wasClick =
        !cancelled &&
        Math.hypot(e.clientX - press.x, e.clientY - press.y) <= CLICK_SLOP_PX &&
        !draggingPiece &&
        !rotatingPiece &&
        !panningFloor &&
        !orbiting;
      if (wasClick) {
        if (press.root) select(press.root, true);
        else select(null, false);
      }
      press = null;
      draggingPiece = false;
      rotatingPiece = false;
      panningFloor = false;
      orbiting = false;
      canvas.classList.remove('is-grabbing');
    }

    function onWheel(e) {
      e.preventDefault();
      userDrove = true;
      var factor = Math.exp(e.deltaY * 0.0012);
      orbit.goalDist = Math.max(MIN_DIST, Math.min(MAX_DIST, orbit.goalDist * factor));
    }

    function onPointerUp(e) {
      endPointer(e, false);
    }
    function onPointerCancel(e) {
      endPointer(e, true);
    }
    function onPrevented(e) {
      e.preventDefault();
    }
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    canvas.addEventListener('pointercancel', onPointerCancel);
    canvas.addEventListener('wheel', onWheel, { passive: false });
    canvas.addEventListener('contextmenu', onPrevented);
    canvas.addEventListener('selectstart', onPrevented);

    function onKey(e) {
      var t = e.target;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      if (e.key === '[' && selectedRoot) {
        selectedRoot.group.rotation.y += Math.PI / 12;
        selectedRoot.manualRotation = true;
      }
      if (e.key === ']' && selectedRoot) {
        selectedRoot.group.rotation.y -= Math.PI / 12;
        selectedRoot.manualRotation = true;
      }
      if (e.shiftKey && selectedRoot) {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          nudgeSelected(-0.2, 0);
          return;
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          nudgeSelected(0.2, 0);
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          nudgeSelected(0, -0.2);
          return;
        }
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          nudgeSelected(0, 0.2);
          return;
        }
      }
      if (e.key === 'ArrowLeft' && ui.prev && !ui.prev.disabled) goToPage(page - 1, -1);
      if (e.key === 'ArrowRight' && ui.next && !ui.next.disabled) goToPage(page + 1, 1);
    }
    window.addEventListener('keydown', onKey);

    /* ---------- frame loop ---------- */
    var raf;
    var lastTick = performance.now();

    function tick() {
      raf = requestAnimationFrame(tick);
      try {
        tickBody();
      } catch (err) {
        /* a throw here would otherwise repeat every frame — bail once */
        fail(err);
      }
    }

    function tickBody() {
      var now = performance.now();
      var dt = Math.min(0.05, (now - lastTick) / 1000);
      lastTick = now;

      /* camera springs */
      var k = reduced ? 1 : 1 - Math.pow(0.0001, dt); /* ~0.12 at 60fps */
      orbit.theta += (orbit.goalTheta - orbit.theta) * k;
      orbit.phi += (orbit.goalPhi - orbit.phi) * k;
      smoothDamp1(orbit.dist, orbit.goalDist, orbit.targetSmooth, dt);
      smoothDampV3(orbit.target, goalTargetV, orbit.velTarget, orbit.targetSmooth, dt);
      applyCamera();

      /* staged pieces spring toward their slots */
      var moveTime = reduced ? 0.0001 : 0.38;
      staged.forEach(function (r) {
        if (draggingPiece && press && press.root === r) return;
        if (r.group.position.distanceToSquared(r.goal) > 1e-6) {
          smoothDampV3(r.group.position, r.goal, r.vel, moveTime, dt);
        }
      });

      /* ghosts slide off and get removed once they settle */
      for (var i = ghosts.length - 1; i >= 0; i--) {
        var g = ghosts[i];
        smoothDampV3(g.group.position, g.goal, g.vel, moveTime, dt);
        if (reduced || g.group.position.distanceToSquared(g.goal) < 0.01) {
          scene.remove(g.group);
          ghosts.splice(i, 1);
        }
      }

      if (selectedRoot && ring.visible) {
        ring.position.x = selectedRoot.group.position.x;
        ring.position.z = selectedRoot.group.position.z;
        ringHandle.position.x =
          selectedRoot.group.position.x + Math.sin(selectedRoot.group.rotation.y) * selectedRoot.radius;
        ringHandle.position.z =
          selectedRoot.group.position.z + Math.cos(selectedRoot.group.rotation.y) * selectedRoot.radius;
        ringHandle.visible = true;
      } else {
        ringHandle.visible = false;
      }

      renderer.render(scene, camera);
      updateTip(); /* after render — projection needs fresh matrices */
    }

    /* ---------- sizing ---------- */

    /* Fit the section to the viewport space under the site header —
       min-height:100dvh alone pushes the bottom bar (filters + carousel)
       below the fold by the header's height, and the wheel zooms the
       stage instead of scrolling, so on desktop the bar would never be
       reachable. Measured from the document, so announcement bars and
       sticky headers are accounted for automatically. */
    function fitHeight() {
      var top = Math.max(0, Math.round(root.getBoundingClientRect().top + window.scrollY));
      /* svh (smallest viewport) rather than dvh: mobile browser bars are
         always excluded, so the bottom chrome can never sit under an
         expanded address bar. */
      root.style.minHeight = 'calc(100svh - ' + top + 'px)';
    }

    function sizeIt() {
      fitHeight();
      var w = stageEl.clientWidth;
      var h = stageEl.clientHeight;
      if (!w || !h) return;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    sizeIt();
    var ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(sizeIt);
      ro.observe(stageEl);
    } else {
      window.addEventListener('resize', sizeIt);
    }
    /* Mobile browser bars collapsing/expanding resize the visual viewport
       without always firing a window resize — re-fit on those too. */
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', sizeIt);
    }

    /* ---------- boot ---------- */
    applyFilters();
    buildMenus();
    syncNav();

    /* A selected piece takes precedence over a serialized page index. */
    var deepPiece = restoredState.piece;
    if (deepPiece) {
      for (var i = 0; i < filtered.length; i++) {
        if (filtered[i].id === deepPiece) {
          page = Math.floor(i / pageSize);
          break;
        }
      }
    }

    tick();
    goToPage(page, 0);

    var deepLinkTimer = 0;
    if (deepPiece) {
      /* select once the trio has staged (goToPage resolves async) */
      var tries = 0;
      deepLinkTimer = setInterval(function () {
        tries++;
        var r = null;
        staged.forEach(function (s) {
          if (s.piece.id === deepPiece) r = s;
        });
        if (r) {
          select(r, true);
          clearInterval(deepLinkTimer);
        } else if (tries > 40) {
          clearInterval(deepLinkTimer);
        }
      }, 250);
    }

    /* entrance choreography (double rAF so initial styles commit first) */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        root.classList.add('cshow--entered');
      });
    });

    /* ---------- teardown ---------- */
    var destroyed = false;
    root._cshowDestroy = function () {
      if (destroyed) return;
      destroyed = true;
      stageToken++;
      loadQueue.length = 0;
      if (deepLinkTimer) clearInterval(deepLinkTimer);
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', sizeIt);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', sizeIt);
      }
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('click', onDocumentClick);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.removeEventListener('pointercancel', onPointerCancel);
      canvas.removeEventListener('wheel', onWheel);
      canvas.removeEventListener('contextmenu', onPrevented);
      canvas.removeEventListener('selectstart', onPrevented);
      canvas.removeEventListener('webglcontextlost', onContextLost);
      /* Keep pageshow while this document remains alive so a context lost
         during bfcache can be rebuilt on return. */
      loadCache.forEach(function (entry) {
        if (entry.scene) disposeObject(entry.scene);
      });
      loadCache.clear();
      scene.traverse(function (o) {
        if (o.geometry) o.geometry.dispose();
        var mats = Array.isArray(o.material) ? o.material : o.material ? [o.material] : [];
        mats.forEach(function (m) {
          m.dispose();
        });
      });
      if (envTexture) envTexture.dispose();
      try {
        renderer.dispose();
        renderer.forceContextLoss && renderer.forceContextLoss();
      } catch (e) {
        /* context may already be lost */
      }
    };

    /* If the GPU context dies anyway (mobile Safari reclaims it under
       memory pressure), degrade to the no-GL product list instead of
       leaving a dead canvas — that's what turns into Safari's
       "a problem repeatedly occurred" reload loop. */
    function onContextLost(e) {
      e.preventDefault();
      fail(new Error('WebGL context lost'));
    }
    canvas.addEventListener('webglcontextlost', onContextLost);

    /* Preserve the live WebGL context when the browser places this page in
       bfcache. The previous eager forceContextLoss strategy was memory-safe
       but made the first Back navigation fragile on mobile Safari. Browsers
       own bfcache memory and evict it under pressure; real navigations still
       dispose every GPU resource below. */
    function onPageHide(e) {
      if (e.persisted) return;
      root._cshowDestroy();
    }
    function onPageShow(e) {
      if (!e.persisted || (!destroyed && !root.classList.contains('catalog-showroom--no-gl'))) return;
      window.removeEventListener('pageshow', onPageShow);
      if (!root.isConnected) return;
      if (!destroyed && root._cshowDestroy) root._cshowDestroy();
      /* A context-lost canvas cannot acquire a fresh context, so rebuild
         from a replacement canvas only when the preserved scene is unusable. */
      var fresh = canvas.cloneNode(false);
      if (canvas.parentNode) canvas.parentNode.replaceChild(fresh, canvas);
      root.classList.remove('catalog-showroom--no-gl');
      delete root.dataset.cshowBound;
      initShowroom(root);
    }
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
  }

  function boot() {
    document.querySelectorAll('[data-catalog-showroom]').forEach(initShowroom);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var root = e.target.querySelector('[data-catalog-showroom]');
    if (root) initShowroom(root);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-catalog-showroom]');
    if (root && root._cshowDestroy) root._cshowDestroy();
  });
})();
